/**
 * lib/internalExams.ts
 * ------------------------------------------------------------------
 * THE SCHOOL'S OWN EXAMS — LKC's End of Year, set by the Examinations Unit.
 * Pure: the screen (features/exams/InternalExams) loads a timetable, shows it
 * against the pupils, and writes the documents this builds.
 *
 * WHERE THE TIMETABLE COMES FROM. Al-Nur's office uploads a workbook; LKC's
 * Examinations Unit publishes PDFs, and the school chose (30 Oct 2026) to load
 * them once rather than build a PDF upload. So a timetable is a file in
 * src/content/examTimetables/ (`lkc-eoy-2026.json`), extracted from
 * "EoY 2026 Timetable.pdf" by its table cells and cross-checked against the
 * four per-form PDFs. Each paper already names its portal subject(s).
 *
 * THREE SESSIONS (AM 07:30, MM 11:00, PM 14:00), not Cambridge's two. A paper
 * printed "AM & MM" — a practical across both — is stored in AM with its own
 * end; "AM 1" / "AM 2" are back-to-back papers in one session, each with its
 * own start. Every paper keeps its own times; the session only groups them.
 *
 * WHO SITS A PAPER is the pupil's enrolment within the form: a Form 4 pupil
 * whose subjects include any of the paper's subjects (Mathematics papers are
 * sat by Mathematics and Mathematics (Core) pupils alike). There are no
 * statements of entry for the school's own exams, so each pupil gets an
 * ordinary `reportExamEntries` statement built from that, and every exam
 * screen written for Cambridge works unchanged.
 *
 * The Examinations Unit's candidate count is kept on each paper
 * (`officeCount`) so the office can see where the portal's enrolment and the
 * Unit's lists disagree before anything is published.
 *
 * Shapes and helpers below the reader are Al-Nur's (lib/internalExams.ts),
 * kept identical so the two schools' documents stay interchangeable.
 * ------------------------------------------------------------------ */
import type { EntryComponent, ExamSession, PupilExamEntries, SessionTimes } from '@/lib/examTimetable';

/* ── the timetable file ───────────────────────────────────────────── */

export interface UnitPaper {
  /** ISO date. */
  date: string;
  session: ExamSession;
  /** As the PDF prints it: 'AM', 'AM & MM', 'MM 2'. */
  printedSession: string;
  start: string;
  end: string;
  minutes: number;
  duration: string;
  form: number;
  /** As printed: "ENGLISH LANGUAGE - Structured (Reading & Writing)". */
  title: string;
  /** "English Language" */
  subject: string;
  /** "Structured (Reading & Writing)" */
  component: string;
  code: string;
  /** Portal subjects whose pupils sit it. */
  subjectIds: string[];
  /** The Examinations Unit's count; null where one cell covered several papers. */
  officeCount: number | null;
  room: string;
}

export interface UnitTimetable {
  id: string;
  kind: 'internal';
  title: string;
  source: string;
  extracted: string;
  sessions: Partial<Record<ExamSession, { start: string; end: string }>>;
  papers: UnitPaper[];
}

/** The series' session times, for screens that want one time per session. */
export function sessionTimesOf(t: UnitTimetable): SessionTimes {
  return {
    AM: t.sessions.AM ?? { start: '07:30', end: '10:30' },
    ...(t.sessions.MM ? { MM: t.sessions.MM } : {}),
    PM: t.sessions.PM ?? { start: '14:00', end: '16:00' },
  };
}

/* ── candidate numbers (Al-Nur's scheme, not issued by LKC yet) ────── */

export interface NumberedPupil { id: string; fullName: string; className: string; form: number }

/** Keep every number already issued; give each pupil without one the next
 *  free number in their form, class by class, in name order. The first is
 *  S{form}001. A number is never reused. */
export function assignCandidateNumbers(pupils: NumberedPupil[], issued: Record<string, string>): Record<string, string> {
  const out = { ...issued };
  const highest = new Map<number, number>();
  for (const n of Object.values(issued)) {
    const m = /^S(\d)(\d{3,})$/.exec(n);
    if (m) highest.set(Number(m[1]), Math.max(highest.get(Number(m[1])) ?? 0, Number(m[2])));
  }
  const fresh = pupils.filter((p) => !out[p.id] && p.form >= 1 && p.form <= 9)
    .sort((a, b) => a.form - b.form || a.className.localeCompare(b.className, undefined, { numeric: true }) || a.fullName.localeCompare(b.fullName));
  for (const p of fresh) {
    const next = (highest.get(p.form) ?? 0) + 1;
    highest.set(p.form, next);
    out[p.id] = `S${p.form}${String(next).padStart(3, '0')}`;
  }
  return out;
}

/* ── what gets written ────────────────────────────────────────────── */

/** A paper's document id. Every slash becomes a dash. */
export const paperDocId = (seriesId: string, date: string, session: ExamSession, code: string) =>
  `${seriesId}__${date}__${session}__${code.replace(/\//g, '-')}`;

export interface InternalPaperDoc {
  id: string; seriesId: string; date: string; session: ExamSession; code: string; subject: string; component: string;
  duration: string; entries: number; start: string; end: string; form: number; subjectId: string; room: string;
  /** LKC: every subject that sits it, and the Examinations Unit's count. */
  subjectIds?: string[];
  officeCount?: number | null;
}

export interface SittingPupil extends NumberedPupil { subjects: string[] }

/** One document per paper, and each pupil's statement. */
export function buildUnitDocs(
  seriesId: string,
  timetable: UnitTimetable,
  pupils: SittingPupil[],
  numbers: Record<string, string>,
): { papers: InternalPaperDoc[]; entries: PupilExamEntries[] } {
  const papers: InternalPaperDoc[] = [];
  const byPupil = new Map<string, EntryComponent[]>();
  for (const p of timetable.papers) {
    const sitters = pupils.filter((x) => x.form === p.form && p.subjectIds.some((s) => x.subjects.includes(s)));
    // Two forms can sit one code in one session (Accounting 0452/01, Forms 3
    // and 4); each is its own paper document, so the id carries the form.
    let id = `${paperDocId(seriesId, p.date, p.session, p.code)}__F${p.form}`;
    let n = 2;
    while (papers.some((x) => x.id === id)) id = `${paperDocId(seriesId, p.date, p.session, p.code)}__F${p.form}__x${n++}`;
    papers.push({
      id, seriesId, date: p.date, session: p.session, code: p.code, subject: p.subject, component: p.component,
      duration: p.duration, entries: sitters.length, start: p.start, end: p.end, form: p.form,
      subjectId: p.subjectIds[0] ?? '', subjectIds: p.subjectIds, officeCount: p.officeCount, room: p.room,
    });
    const [syllabus, ...rest] = p.code.split('/');
    for (const x of sitters) {
      const list = byPupil.get(x.id) ?? [];
      list.push({
        code: p.code, syllabus, component: rest.join('/'), subject: p.subject, title: p.component || p.subject,
        date: p.date, session: p.session, sitting: { start: p.start, end: p.end },
      });
      byPupil.set(x.id, list);
    }
  }
  const entries: PupilExamEntries[] = [];
  for (const x of pupils) {
    const components = byPupil.get(x.id);
    if (!components?.length) continue;
    entries.push({
      seriesId, studentId: x.id, candidateNumber: numbers[x.id] ?? '', candidateName: x.fullName,
      components: components.sort((a, b) => a.date.localeCompare(b.date)
        || String(a.session).localeCompare(String(b.session))
        || (a.sitting?.start ?? '').localeCompare(b.sitting?.start ?? '')),
      importedAt: '', source: timetable.source,
    });
  }
  return { papers, entries };
}

/** Papers where the portal's enrolment and the Unit's count disagree by more
 *  than a few pupils — what the office should look at before publishing. */
export function countGaps(papers: InternalPaperDoc[], tolerance = 3): InternalPaperDoc[] {
  return papers.filter((p) => p.officeCount != null
    && Math.abs(p.entries - p.officeCount) > Math.max(tolerance, Math.round(p.officeCount * 0.1)));
}

/** What a re-load changes, paper by paper, for the screen to show. */
export function changesBetween(before: InternalPaperDoc[], after: InternalPaperDoc[]): { added: InternalPaperDoc[]; removed: InternalPaperDoc[]; moved: { from: InternalPaperDoc; to: InternalPaperDoc }[] } {
  const key = (p: InternalPaperDoc) => `${p.form}|${p.code}|${p.component.toLowerCase()}`;
  const was = new Map(before.map((p) => [key(p), p]));
  const now = new Map(after.map((p) => [key(p), p]));
  const moved: { from: InternalPaperDoc; to: InternalPaperDoc }[] = [];
  for (const [k, p] of now) {
    const o = was.get(k);
    if (o && (o.date !== p.date || o.session !== p.session || o.start !== p.start || o.end !== p.end)) moved.push({ from: o, to: p });
  }
  return {
    added: after.filter((p) => !was.has(key(p))),
    removed: before.filter((p) => !now.has(key(p))),
    moved,
  };
}
